import { ApiError } from './apiError';
import type { ApiFailure, ApiSuccess, Pagination, Session } from '../types/api';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

/**
 * El access token vive solo en memoria.
 *
 * No se guarda en `localStorage` ni en `sessionStorage`: cualquier script que
 * llegue a ejecutarse en la pagina podria leerlo desde ahi. La sesion se
 * recupera al recargar mediante la cookie de refresh, que es httpOnly y por
 * tanto inaccesible desde JavaScript.
 */
let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onUnauthenticated(callback: (() => void) | null): void {
  onSessionLost = callback;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  /** Evita el reintento con refresco. Lo usan los propios extremos de sesion. */
  skipRefresh?: boolean;
}

export interface PaginatedResult<T> {
  data: T[];
  pagination: Pagination;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path}`;
  if (!query) {
    return url;
  }

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') {
      params.append(key, String(value));
    }
  }

  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

async function parseFailure(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as ApiFailure;
    return new ApiError(
      response.status,
      body.error?.code ?? 'INTERNAL_ERROR',
      body.error?.message ?? 'Ha ocurrido un error inesperado.',
      body.error?.details ?? [],
    );
  } catch {
    return new ApiError(
      response.status,
      'INTERNAL_ERROR',
      'El servidor devolvio una respuesta que no se pudo interpretar.',
    );
  }
}

/**
 * Las subidas del chofer viajan como `FormData`.
 *
 * En ese caso **no** se fija `Content-Type`: el navegador tiene que escribirlo
 * el mismo para incluir el separador del `multipart`, y ponerlo a mano deja un
 * cuerpo que el servidor no sabe partir.
 */
function isFormData(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

function requestBody(body: unknown): BodyInit | undefined {
  if (body === undefined) {
    return undefined;
  }
  return isFormData(body) ? body : JSON.stringify(body);
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const multipart = isFormData(options.body);

  if (options.body !== undefined && !multipart) {
    headers['Content-Type'] = 'application/json';
  }
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  try {
    return await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      // Necesario para que viaje la cookie de refresh.
      credentials: 'include',
      body: requestBody(options.body),
      signal: options.signal ?? null,
    });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'No hay conexion con el servidor.');
  }
}

/**
 * Refresco en vuelo unico.
 *
 * Si cinco peticiones reciben 401 a la vez, solo una llama a `/auth/refresh` y
 * las demas esperan su resultado. Sin esto, las cinco intentarian rotar el
 * mismo refresh token y el backend interpretaria la carrera como reutilizacion,
 * revocando la familia entera y expulsando al usuario.
 */
let refreshInFlight: Promise<Session | null> | null = null;

async function performRefresh(): Promise<Session | null> {
  const response = await send('/auth/refresh', { method: 'POST', skipRefresh: true });

  if (!response.ok) {
    accessToken = null;
    return null;
  }

  const body = (await response.json()) as ApiSuccess<Session>;
  accessToken = body.data.access_token;
  return body.data;
}

function refreshSession(): Promise<Session | null> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<ApiSuccess<T>> {
  let response = await send(path, options);

  if (response.status === 401 && !options.skipRefresh) {
    const refreshed = await refreshSession();

    if (refreshed === null) {
      onSessionLost?.();
      throw await parseFailure(response);
    }

    response = await send(path, options);
  }

  if (response.status === 401) {
    onSessionLost?.();
    throw await parseFailure(response);
  }

  if (!response.ok) {
    throw await parseFailure(response);
  }

  if (response.status === 204) {
    return { success: true, data: undefined as T };
  }

  return (await response.json()) as ApiSuccess<T>;
}

export const api = {
  async get<T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return (await request<T>(path, { ...options, method: 'GET' })).data;
  },

  async getPaginated<T>(
    path: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<PaginatedResult<T>> {
    const body = await request<T[]>(path, { ...options, method: 'GET' });
    return {
      data: body.data,
      pagination: body.pagination ?? {
        page: 1,
        limit: body.data.length,
        total: body.data.length,
        total_pages: 1,
      },
    };
  },

  async post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return (await request<T>(path, { ...options, method: 'POST', body })).data;
  },

  async patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return (await request<T>(path, { ...options, method: 'PATCH', body })).data;
  },

  async put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return (await request<T>(path, { ...options, method: 'PUT', body })).data;
  },

  async delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return (await request<T>(path, { ...options, method: 'DELETE' })).data;
  },

  /** Restaura la sesion al cargar la aplicacion, usando solo la cookie. */
  refreshSession,
};
